import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA,
  AVERAGE_CTR,
  GLOBAL_REPORT_STATS_CREATIVES_HIGHEST_VOLUME_TITLE,
  GLOBAL_REPORT_STATS_CREATIVES_LOWEST_CPA_TITLE,
  GLOBAL_REPORT_STATS_CREATIVES_TITLE,
  GLOBAL_REPORT_STATS_NO_DATA,
  GLOBAL_REPORT_STATS_PLATFORM_CPA_TITLE,
  GLOBAL_REPORT_STATS_PLATFORM_SPEND_TITLE,
  GLOBAL_REPORT_STATS_PLATFORM_SUBTITLE,
  GLOBAL_REPORT_STATS_PLATFORM_TITLE,
  GLOBAL_REPORT_STATS_RADAR_SUBTITLE,
  GLOBAL_REPORT_STATS_RADAR_TITLE,
  GLOBAL_REPORT_STATS_SPEND_AXIS,
  GLOBAL_REPORT_STATS_TIME_TITLE,
  RESULTS,
  TOTAL_IMPRESSIONS,
  TOTAL_SPEND,
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../i18n/keysTranslations";
import AreaChart from "../../../components/Charts/AreaChart";
import BarChart from "../../../components/Charts/BarChart";
import PieChart from "../../../components/Charts/PieChart";
import RadarChart from "../../../components/Charts/RadarChart";
import {
  buildGlobalReportAreaChartDataset,
  buildGlobalReportDailyResultsDataset,
  buildGlobalReportImpressionsCtrAreaDataset,
  buildGlobalReportCampaignRadarDataset,
  buildGlobalReportCreativeRankingDatasets,
  buildGlobalReportPlatformComparisonDataset,
} from "../utils/buildGlobalReportChartDatasets";
import useGlobalReportCampaignDetails from "../hooks/useGlobalReportCampaignDetails";
import GlobalReportCreativeBarTooltip from "./GlobalReportCreativeBarTooltip";
import GlobalReportCreativeAxisTick, {
  GlobalReportPdfCreativeVideos,
  isVideoMediaUrl,
} from "./GlobalReportCreativeAxisTick";
import getGlobalReportTimeChartCopyKeys from "../utils/getGlobalReportTimeChartCopyKeys";
import getGlobalReportResultsBarChartLayout from "../utils/getGlobalReportResultsBarChartLayout";
import translateGlobalReportPlatform from "../utils/translateGlobalReportPlatform";

/** Perfil multidimensional (radar): oculto temporalmente */
const SHOW_GLOBAL_REPORT_RADAR_CHART = false;
const GLOBAL_REPORT_STATS_BAR_SIZE = 80;

const sectionSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const ChartSkeleton = ({ height = 280 }) => (
  <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />
);

const StatisticsSection = ({ title, subtitle, children, captureId }) => (
  <Box sx={sectionSx} data-pdf-capture-id={captureId}>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const ChartBlock = ({ title, subtitle, children, sx, captureId }) => (
  <Box sx={{ mt: 3, ...sx }} data-pdf-capture-id={captureId}>
    {title ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
    ) : null}
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const GlobalReportStatisticsSection = ({
  campaigns = {},
  statistics = {},
  breakdown = "weekly",
  isLoadingStatistics = false,
  isLoadingAdsStatistics = false,
  isLoadingCampaigns = false,
  isStatisticsSettledForRange = false,
  isAdsStatisticsSettledForRange = false,
  onCaptureReadyChange,
  onCampaignDetailsChange,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  /** Time/platform charts: first campaign stats fetch */
  const isCampaignChartsReady =
    isStatisticsSettledForRange && !isLoadingCampaigns && !isLoadingStatistics;
  /** Creatives: ads maximum fetch (+ campaign details for labels) */
  const isCreativesReady =
    isCampaignChartsReady &&
    isAdsStatisticsSettledForRange &&
    !isLoadingAdsStatistics;

  const {
    campaignDetails,
    isLoadingCampaignDetails,
    resolveAdLabel,
    resolveAdMediaUrls,
  } = useGlobalReportCampaignDetails({
    campaigns,
    enabled: isCampaignChartsReady,
  });

  const translatePlatform = useCallback(
    (platform) => translateGlobalReportPlatform(platform, t),
    [t]
  );

  const areaDataset = useMemo(() => {
    const built = buildGlobalReportAreaChartDataset({
      campaigns,
      statistics,
      dispatch,
      breakdown,
    });
    return {
      ...built,
      chartConfig: {
        spend: {
          ...built.chartConfig.spend,
          label: t(GLOBAL_REPORT_STATS_SPEND_AXIS),
        },
        mainAction: {
          ...built.chartConfig.mainAction,
          label: t(RESULTS),
        },
      },
    };
  }, [breakdown, campaigns, dispatch, statistics, t]);

  const dailyResultsDataset = useMemo(() => {
    const built = buildGlobalReportDailyResultsDataset({
      campaigns,
      statistics,
      dispatch,
      breakdown,
    });
    return {
      ...built,
      chartConfig: {
        mainAction: {
          ...built.chartConfig.mainAction,
          label: t(RESULTS),
        },
      },
    };
  }, [breakdown, campaigns, dispatch, statistics, t]);

  const impressionsCtrDataset = useMemo(() => {
    const built = buildGlobalReportImpressionsCtrAreaDataset({
      campaigns,
      statistics,
      dispatch,
      breakdown,
    });
    return {
      ...built,
      chartConfig: {
        impressions: {
          ...built.chartConfig.impressions,
          label: t(TOTAL_IMPRESSIONS),
        },
        ctr: {
          ...built.chartConfig.ctr,
          label: t(AVERAGE_CTR),
        },
      },
    };
  }, [breakdown, campaigns, dispatch, statistics, t]);

  const platformDataset = useMemo(() => {
    const built = buildGlobalReportPlatformComparisonDataset({
      campaigns,
      statistics,
    });

    return {
      ...built,
      barChartData: built.barChartData.map((row) => ({
        ...row,
        label: translatePlatform(row.platform),
      })),
      cpaBarChartData: built.cpaBarChartData.map((row) => ({
        ...row,
        label: translatePlatform(row.platform),
      })),
      pieChartData: built.pieChartData,
      pieChartConfig: Object.fromEntries(
        Object.entries(built.pieChartConfig).map(([platform, config]) => [
          platform,
          { ...config, label: translatePlatform(platform) },
        ])
      ),
      barChartConfig: {
        value: {
          ...built.barChartConfig.value,
          label: t(RESULTS),
        },
      },
      cpaBarChartConfig: {
        value: {
          ...built.cpaBarChartConfig.value,
          label: t(ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA),
        },
      },
      activePlatformCount: built.activePlatformCount,
    };
  }, [campaigns, statistics, t, translatePlatform]);

  const creativeDatasets = useMemo(() => {
    const built = buildGlobalReportCreativeRankingDatasets({
      campaigns,
      statistics,
      campaignDetails,
      resolveAdLabel,
      resolveAdMediaUrls,
    });
    return {
      lowestCpa: {
        ...built.lowestCpa,
        chartConfig: {
          value: {
            ...built.lowestCpa.chartConfig.value,
            label: t(ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA),
          },
        },
      },
      highestVolume: {
        ...built.highestVolume,
        chartConfig: {
          value: {
            ...built.highestVolume.chartConfig.value,
            label: t(RESULTS),
          },
        },
      },
    };
  }, [
    campaigns,
    statistics,
    campaignDetails,
    resolveAdLabel,
    resolveAdMediaUrls,
    t,
  ]);

  const formatCurrency = (value, currency) =>
    currency
      ? t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, { value, currency })
      : String(value);

  const renderLowestCpaCreativeTooltip = useCallback(
    (tooltipProps) => (
      <GlobalReportCreativeBarTooltip
        {...tooltipProps}
        metricLabel={t(ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA)}
        formatMetricValue={(value) =>
          value > 0
            ? formatCurrency(value, platformDataset.primaryCurrency || "CLP")
            : "—"
        }
        translatePlatform={translatePlatform}
      />
    ),
    // eslint-disable-next-line
    [t, platformDataset.primaryCurrency, translatePlatform]
  );

  const renderHighestVolumeCreativeTooltip = useCallback(
    (tooltipProps) => (
      <GlobalReportCreativeBarTooltip
        {...tooltipProps}
        metricLabel={t(RESULTS)}
        formatMetricValue={(value) => t(VALUE_NUMBER, { value: value ?? 0 })}
        translatePlatform={translatePlatform}
      />
    ),
    [t, translatePlatform]
  );

  const formatLowestCpaMetricValue = useCallback(
    (value) =>
      value > 0
        ? formatCurrency(value, platformDataset.primaryCurrency || "CLP")
        : "—",
    // eslint-disable-next-line
    [platformDataset.primaryCurrency, t]
  );

  const renderLowestCpaCreativeAxisTick = useCallback(
    (tickProps) => (
      <GlobalReportCreativeAxisTick
        {...tickProps}
        metricLabel={t(ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA)}
        formatMetricValue={formatLowestCpaMetricValue}
        translatePlatform={translatePlatform}
      />
    ),
    [formatLowestCpaMetricValue, t, translatePlatform]
  );

  const renderHighestVolumeCreativeAxisTick = useCallback(
    (tickProps) => (
      <GlobalReportCreativeAxisTick
        {...tickProps}
        metricLabel={t(RESULTS)}
        formatMetricValue={(value) => t(VALUE_NUMBER, { value: value ?? 0 })}
        translatePlatform={translatePlatform}
      />
    ),
    [t, translatePlatform]
  );

  const radarDataset = useMemo(() => {
    if (!SHOW_GLOBAL_REPORT_RADAR_CHART) {
      return { chartData: [], chartConfig: {} };
    }
    return buildGlobalReportCampaignRadarDataset({
      campaigns,
      statistics,
      metricLabels: {
        impressions: t(TOTAL_IMPRESSIONS),
        ctr: t(AVERAGE_CTR),
        spend: t(TOTAL_SPEND),
        mainAction: t(RESULTS),
        cpa: t(ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA),
      },
    });
  }, [campaigns, statistics, t]);
  const isCaptureReady = isCreativesReady && !isLoadingCampaignDetails;

  useEffect(() => {
    if (typeof onCampaignDetailsChange === "function") {
      onCampaignDetailsChange(campaignDetails);
    }
  }, [campaignDetails, onCampaignDetailsChange]);

  useEffect(() => {
    if (typeof onCaptureReadyChange !== "function") {
      return undefined;
    }

    onCaptureReadyChange(isCaptureReady);
  }, [isCaptureReady, onCaptureReadyChange]);

  // Reset only on unmount — cleanup-on-deps pulsed false and re-triggered PDF prewarm.
  useEffect(() => {
    if (typeof onCaptureReadyChange !== "function") {
      return undefined;
    }

    return () => onCaptureReadyChange(false);
  }, [onCaptureReadyChange]);

  const hasAreaData = areaDataset.chartData.length > 0;
  const hasDailyResultsData = dailyResultsDataset.chartData.length > 0;
  const hasImpressionsCtrData = impressionsCtrDataset.chartData.length > 0;
  const hasPlatformBarData = platformDataset.barChartData.length > 0;
  const hasPlatformCpaBarData = platformDataset.cpaBarChartData.length > 0;
  const hasPlatformPieData = platformDataset.pieChartData.length > 0;
  const hasPlatformData =
    hasPlatformBarData || hasPlatformCpaBarData || hasPlatformPieData;
  const showPlatformComparison = (platformDataset.activePlatformCount ?? 0) > 1;
  const hasCreativeLowestCpaData =
    creativeDatasets.lowestCpa.chartData.length > 0;
  const hasCreativeHighestVolumeData =
    creativeDatasets.highestVolume.chartData.length > 0;
  const hasCreativeData =
    hasCreativeLowestCpaData || hasCreativeHighestVolumeData;
  const hasRadarData = radarDataset.chartData.length > 0;

  const timeChartCopyKeys = useMemo(
    () => getGlobalReportTimeChartCopyKeys({ breakdown }),
    [breakdown]
  );

  const resultsBarChartLayout = useMemo(
    () => getGlobalReportResultsBarChartLayout({ breakdown }),
    [breakdown]
  );

  const pdfCreativeVideoUrls = useMemo(() => {
    const rows = [
      ...(creativeDatasets.lowestCpa?.chartData || []),
      ...(creativeDatasets.highestVolume?.chartData || []),
    ];
    return rows.flatMap((row) =>
      (row.mediaUrls || []).filter((url) => isVideoMediaUrl(url))
    );
  }, [creativeDatasets]);

  const creativesSection = (
    <StatisticsSection title={t(GLOBAL_REPORT_STATS_CREATIVES_TITLE)}>
      <GlobalReportPdfCreativeVideos urls={pdfCreativeVideoUrls} />
      {!isCreativesReady || isLoadingCampaignDetails ? (
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            lg={6}
            data-pdf-capture-id="global-report-creative-lowest-cpa-card"
          >
            <ChartSkeleton height={320} />
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            data-pdf-capture-id="global-report-creative-highest-volume-card"
          >
            <ChartSkeleton height={320} />
          </Grid>
        </Grid>
      ) : hasCreativeData ? (
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            lg={6}
            data-pdf-capture-id="global-report-creative-lowest-cpa-card"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t(GLOBAL_REPORT_STATS_CREATIVES_LOWEST_CPA_TITLE)}
            </Typography>
            {hasCreativeLowestCpaData ? (
              <BarChart
                embedded
                hasCartesianGrid={false}
                chartData={creativeDatasets.lowestCpa.chartData}
                chartConfig={{
                  value: {
                    ...creativeDatasets.lowestCpa.chartConfig.value,
                    valueFormatter: formatLowestCpaMetricValue,
                  },
                }}
                layout="horizontal"
                stacked={false}
                valueKey="value"
                renderTooltip={renderLowestCpaCreativeTooltip}
                renderCategoryAxisTick={renderLowestCpaCreativeAxisTick}
                categoryAxisWidth={52}
                height={Math.max(
                  280,
                  creativeDatasets.lowestCpa.chartData.length * 48
                )}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(GLOBAL_REPORT_STATS_NO_DATA)}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            data-pdf-capture-id="global-report-creative-highest-volume-card"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t(GLOBAL_REPORT_STATS_CREATIVES_HIGHEST_VOLUME_TITLE)}
            </Typography>
            {hasCreativeHighestVolumeData ? (
              <BarChart
                embedded
                hasCartesianGrid={false}
                chartData={creativeDatasets.highestVolume.chartData}
                chartConfig={creativeDatasets.highestVolume.chartConfig}
                layout="horizontal"
                stacked={false}
                valueKey="value"
                renderTooltip={renderHighestVolumeCreativeTooltip}
                renderCategoryAxisTick={renderHighestVolumeCreativeAxisTick}
                categoryAxisWidth={52}
                height={Math.max(
                  280,
                  creativeDatasets.highestVolume.chartData.length * 48
                )}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(GLOBAL_REPORT_STATS_NO_DATA)}
              </Typography>
            )}
          </Grid>
        </Grid>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t(GLOBAL_REPORT_STATS_NO_DATA)}
        </Typography>
      )}
    </StatisticsSection>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StatisticsSection title={t(GLOBAL_REPORT_STATS_TIME_TITLE)}>
        {!isCampaignChartsReady ? (
          <>
            <ChartSkeleton height={360} />
            <Box sx={{ mt: 3 }}>
              <ChartSkeleton height={360} />
            </Box>
            <Box sx={{ mt: 3 }}>
              <ChartSkeleton height={360} />
            </Box>
          </>
        ) : (
          <>
            <ChartBlock
              title={t(timeChartCopyKeys.resultsTitle)}
              sx={{ mt: 0 }}
              captureId="global-report-daily-results-card"
            >
              {hasDailyResultsData ? (
                <BarChart
                  embedded
                  hasCartesianGrid={false}
                  barSize={resultsBarChartLayout.barSize}
                  maxBarSize={resultsBarChartLayout.maxBarSize}
                  barCategoryGap={resultsBarChartLayout.barCategoryGap}
                  chartData={dailyResultsDataset.chartData}
                  chartConfig={{
                    mainAction: {
                      ...dailyResultsDataset.chartConfig.mainAction,
                      valueFormatter: (value) => t(VALUE_NUMBER, { value }),
                    },
                  }}
                  stacked={false}
                  valueKey="mainAction"
                  hasLegend={false}
                  height={360}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t(GLOBAL_REPORT_STATS_NO_DATA)}
                </Typography>
              )}
            </ChartBlock>

            <ChartBlock
              subtitle={t(timeChartCopyKeys.timeSubtitle)}
              captureId="global-report-spend-results-card"
            >
              {hasAreaData ? (
                <AreaChart
                  embedded
                  hasCartesianGrid={false}
                  chartData={areaDataset.chartData}
                  chartConfig={{
                    ...areaDataset.chartConfig,
                    spend: {
                      ...areaDataset.chartConfig.spend,
                      valueFormatter: (value) =>
                        formatCurrency(value, areaDataset.primaryCurrency),
                    },
                  }}
                  title={null}
                  subtitle={null}
                  height={360}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t(GLOBAL_REPORT_STATS_NO_DATA)}
                </Typography>
              )}
            </ChartBlock>

            <ChartBlock
              subtitle={t(timeChartCopyKeys.impressionsCtrSubtitle)}
              captureId="global-report-impressions-ctr-card"
            >
              {hasImpressionsCtrData ? (
                <AreaChart
                  embedded
                  hasCartesianGrid={false}
                  chartData={impressionsCtrDataset.chartData}
                  chartConfig={{
                    ...impressionsCtrDataset.chartConfig,
                    impressions: {
                      ...impressionsCtrDataset.chartConfig.impressions,
                      valueFormatter: (value) => t(VALUE_NUMBER, { value }),
                    },
                    ctr: {
                      ...impressionsCtrDataset.chartConfig.ctr,
                      valueFormatter: (value) =>
                        t(VALUE_WITH_PERCENTAGE, { value }),
                    },
                  }}
                  title={null}
                  subtitle={null}
                  height={360}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t(GLOBAL_REPORT_STATS_NO_DATA)}
                </Typography>
              )}
            </ChartBlock>
          </>
        )}
      </StatisticsSection>

      {!isCampaignChartsReady ? (
        <StatisticsSection title={t(GLOBAL_REPORT_STATS_PLATFORM_TITLE)}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <ChartSkeleton />
            </Grid>
            <Grid item xs={12} lg={5}>
              <ChartSkeleton />
            </Grid>
            <Grid item xs={12} lg={7}>
              <ChartSkeleton />
            </Grid>
          </Grid>
        </StatisticsSection>
      ) : showPlatformComparison ? (
        <StatisticsSection title={t(GLOBAL_REPORT_STATS_PLATFORM_TITLE)}>
          {hasPlatformData ? (
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                lg={7}
                data-pdf-capture-id="global-report-platform-results-bar-card"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {t(GLOBAL_REPORT_STATS_PLATFORM_SUBTITLE)}
                </Typography>
                {hasPlatformBarData ? (
                  <BarChart
                    embedded
                    hasCartesianGrid={false}
                    barSize={GLOBAL_REPORT_STATS_BAR_SIZE}
                    chartData={platformDataset.barChartData}
                    chartConfig={{
                      value: {
                        ...platformDataset.barChartConfig.value,
                        valueFormatter: (value) =>
                          t(VALUE_NUMBER, { value: value ?? 0 }),
                      },
                    }}
                    stacked={false}
                    valueKey="value"
                    hasLegend={false}
                    height={300}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t(GLOBAL_REPORT_STATS_NO_DATA)}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                lg={5}
                data-pdf-capture-id="global-report-platform-spend-pie-card"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {t(GLOBAL_REPORT_STATS_PLATFORM_SPEND_TITLE)}
                </Typography>
                <PieChart
                  embedded
                  hasCartesianGrid={false}
                  showTotal={false}
                  chartData={platformDataset.pieChartData}
                  chartConfig={{
                    value: {
                      valueFormatter: (value, payload) =>
                        formatCurrency(
                          value,
                          payload?.currency ||
                            platformDataset.primaryCurrency ||
                            "CLP"
                        ),
                    },
                    ...platformDataset.pieChartConfig,
                  }}
                  labelKey="label"
                  valueKey="value"
                />
              </Grid>
              <Grid
                item
                xs={12}
                lg={7}
                data-pdf-capture-id="global-report-platform-cpa-bar-card"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {t(GLOBAL_REPORT_STATS_PLATFORM_CPA_TITLE)}
                </Typography>
                {hasPlatformCpaBarData ? (
                  <BarChart
                    embedded
                    hasCartesianGrid={false}
                    barSize={GLOBAL_REPORT_STATS_BAR_SIZE}
                    chartData={platformDataset.cpaBarChartData}
                    chartConfig={{
                      value: {
                        ...platformDataset.cpaBarChartConfig.value,
                        valueFormatter: (value, payload) =>
                          formatCurrency(
                            value,
                            payload?.currency ||
                              platformDataset.primaryCurrency ||
                              "CLP"
                          ),
                      },
                    }}
                    stacked={false}
                    valueKey="value"
                    hasLegend={false}
                    height={300}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t(GLOBAL_REPORT_STATS_NO_DATA)}
                  </Typography>
                )}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(GLOBAL_REPORT_STATS_NO_DATA)}
            </Typography>
          )}
        </StatisticsSection>
      ) : null}

      {creativesSection}

      {SHOW_GLOBAL_REPORT_RADAR_CHART ? (
        <StatisticsSection
          title={t(GLOBAL_REPORT_STATS_RADAR_TITLE)}
          subtitle={t(GLOBAL_REPORT_STATS_RADAR_SUBTITLE)}
          captureId="global-report-radar-card"
        >
          {!isCampaignChartsReady ? (
            <ChartSkeleton height={380} />
          ) : hasRadarData ? (
            <RadarChart
              embedded
              hasPolarGrid={false}
              chartData={radarDataset.chartData}
              chartConfig={radarDataset.chartConfig}
              height={400}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(GLOBAL_REPORT_STATS_NO_DATA)}
            </Typography>
          )}
        </StatisticsSection>
      ) : null}
    </Box>
  );
};

export default GlobalReportStatisticsSection;
